/**
 * Types for the Platform Assistant Chatbot feature.
 */

/**
 * Context item type constants using namespaced convention.
 * Backend stores these as strings for maximum extensibility.
 */
export const CONTEXT_ITEM_TYPES = {
  ADO_WORKITEM: 'ado:workitem',
  ADO_PR: 'ado:pr',
  ADO_REPO: 'ado:repo',
  ADO_BRANCH: 'ado:branch',
  WIKI_PAGE: 'wiki:page',
  FILE: 'file',
  URL: 'url',
  NOTE: 'note',
} as const;

/**
 * Project-level context item types for knowledge base.
 * These types are specifically for project context that applies to all sessions.
 */
export const PROJECT_CONTEXT_ITEM_TYPES = {
  // Wiki/Documentation types
  WIKI_ADR: 'wiki:adr',           // Architecture Decision Records
  WIKI_ARCH: 'wiki:arch',         // Architecture documentation
  WIKI_GUIDE: 'wiki:guide',       // How-to guides
  WIKI_RUNBOOK: 'wiki:runbook',   // Operational runbooks
  
  // Work item types
  EPIC: 'epic',                   // Epic work items
  FEATURE: 'feature',             // Feature work items
  
  // External documentation types
  DOC_EXTERNAL: 'doc:external',   // External documentation links
  DOC_API: 'doc:api',             // API documentation
  DOC_DESIGN: 'doc:design',       // Design documents
  
  // Inherited from session context
  ADO_WORKITEM: 'ado:workitem',
  WIKI_PAGE: 'wiki:page',
  FILE: 'file',
  URL: 'url',
  NOTE: 'note',
} as const;

export type ContextItemType = typeof CONTEXT_ITEM_TYPES[keyof typeof CONTEXT_ITEM_TYPES] | string;
export type ProjectContextItemType = typeof PROJECT_CONTEXT_ITEM_TYPES[keyof typeof PROJECT_CONTEXT_ITEM_TYPES] | string;

/**
 * Generic context item for session context.
 * Uses type/value strings for maximum extensibility without schema changes.
 */
export interface SessionContextItem {
  /** Type using namespaced convention: "ado:workitem", "wiki:page", "file", etc. */
  type: string;
  /** Value/identifier: "12345" (work item), "/docs/arch" (wiki), "src/auth.ts" (file) */
  value: string;
  /** When the item was added */
  addedAt: string;
}

/**
 * Chatbot session metadata.
 */
export interface ChatbotSession {
  sessionId: string;
  title?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessage?: string;
  /** Optional linked project ID for context injection */
  projectId?: string;
  /** Project name (populated by API for display) */
  projectName?: string;
  /** Generic context items for conversational context */
  contextItems?: SessionContextItem[];
}

/**
 * Chat message in a conversation.
 */
export interface ChatbotMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  toolCalls?: ChatbotToolCall[];
  /**
   * The turn's reasoning-summary text (Responses-path models only), captured live and frozen onto the
   * finalized assistant message. In-memory only — NOT persisted, so it is absent on turns reloaded from
   * history. Drives the "Thought process" disclosure (chatbot-thinking-ux).
   */
  reasoning?: string;
  /**
   * Ordered tool steps for the turn (derived from {@link toolCalls}). Same lifetime/persistence as
   * {@link reasoning}. Each maps onto a reused `ToolResultCard` in the disclosure.
   */
  steps?: ThoughtStep[];
  /** Client-measured seconds the turn spent before completing — the "Thought for {Xs}" label. In-memory only. */
  thinkingSeconds?: number;
}

/**
 * Per-turn reasoning effort the user can pick in the chat input (chatbot reasoning-effort selector).
 * Transient: chosen per turn, sent verbatim with each message, never persisted. `'off'` forces reasoning
 * off for the turn; `'low' | 'medium' | 'high'` request that level. Only observable on the Responses
 * chatbot path (where reasoning summaries are surfaced).
 */
export type ReasoningEffort = 'off' | 'low' | 'medium' | 'high';

/** One selectable reasoning-effort level for the input dropdown. */
export interface ReasoningEffortOption {
  value: ReasoningEffort;
  label: string;
  /** Short helper shown beside the label in the dropdown. */
  hint: string;
}

/** Selectable reasoning-effort levels, ordered light → heavy. */
export const REASONING_EFFORT_OPTIONS: ReasoningEffortOption[] = [
  { value: 'off', label: 'Off', hint: 'No reasoning' },
  { value: 'low', label: 'Low', hint: 'Light reasoning' },
  { value: 'medium', label: 'Medium', hint: 'More reasoning' },
  { value: 'high', label: 'High', hint: 'Deepest reasoning' },
];

/** Default per-turn effort: on, but light. */
export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'low';

/**
 * Tool call made by the assistant.
 */
export interface ChatbotToolCall {
  id: string;
  name: string;
  arguments?: string;
  result?: string;
}

/**
 * One tool step shown in the assistant's "Thought process" disclosure (chatbot-thinking-ux).
 * Derived from a {@link ChatbotToolCall}; shape maps 1:1 onto the reused `ToolResultCard`
 * (`name → toolName`, `status === 'running' → isLoading`, `error`, `resultHint → child`).
 */
export interface ThoughtStep {
  id: string;
  name: string;
  status: 'running' | 'done' | 'error';
  /** Short one-line preview of the tool result (first non-empty line, truncated). */
  resultHint?: string;
  /** Error text when the step failed. */
  error?: string;
}

/**
 * SSE event types from the chatbot backend.
 */
export type ChatbotEventType = 
  | 'messagestart'
  | 'token'
  | 'toolcall'
  | 'toolresult'
  | 'messageend'
  | 'error';

/**
 * Base SSE event from chatbot.
 */
export interface ChatbotBaseEvent {
  type: ChatbotEventType;
}

/**
 * Message start event - beginning of assistant response.
 */
export interface ChatbotMessageStartEvent extends ChatbotBaseEvent {
  type: 'messagestart';
  sessionId: string;
}

/**
 * Token event - text chunk from assistant.
 */
export interface ChatbotTokenEvent extends ChatbotBaseEvent {
  type: 'token';
  content: string;
}

/**
 * Tool call event - assistant is calling a tool.
 */
export interface ChatbotToolCallEvent extends ChatbotBaseEvent {
  type: 'toolcall';
  toolName: string;
  toolCallId: string;
  toolArguments?: string;
}

/**
 * Tool result event - result of a tool call.
 */
export interface ChatbotToolResultEvent extends ChatbotBaseEvent {
  type: 'toolresult';
  toolCallId: string;
  content: string;
}

/**
 * Message end event - response complete.
 */
export interface ChatbotMessageEndEvent extends ChatbotBaseEvent {
  type: 'messageend';
}

/**
 * Error event - something went wrong.
 */
export interface ChatbotErrorEvent extends ChatbotBaseEvent {
  type: 'error';
  error: string;
}

/**
 * Union type for all chatbot SSE events.
 */
export type ChatbotEvent =
  | ChatbotMessageStartEvent
  | ChatbotTokenEvent
  | ChatbotToolCallEvent
  | ChatbotToolResultEvent
  | ChatbotMessageEndEvent
  | ChatbotErrorEvent;

/**
 * Request to create a new chatbot session.
 */
export interface CreateChatbotSessionRequest {
  title?: string;
  /** Optional project ID to link session for context injection */
  projectId?: string;
  /** Optional context items to attach at session creation */
  contextItems?: { type: string; value: string }[];
}

/**
 * Response from creating a chatbot session.
 */
export interface CreateChatbotSessionResponse {
  sessionId: string;
  createdAt: string;
  title?: string;
}

/**
 * Response from listing chatbot sessions.
 */
export interface ListChatbotSessionsResponse {
  sessions: ChatbotSession[];
  total: number;
  hasMore: boolean;
}

/**
 * Request to add a context item to a session.
 */
export interface AddContextItemRequest {
  /** Type using namespaced convention: "ado:workitem", "wiki:page", etc. */
  type: string;
  /** Value/identifier for the item */
  value: string;
}

/**
 * Response from adding a context item.
 */
export interface AddContextItemResponse {
  sessionId: string;
  contextItem: SessionContextItem;
}

/**
 * Request to remove a context item from a session.
 */
export interface RemoveContextItemRequest {
  /** Type of context item to remove */
  type: string;
  /** Value of context item to remove */
  value: string;
}

/**
 * Callback for handling chatbot SSE events.
 */
export type ChatbotEventHandler = (event: ChatbotEvent) => void;

/**
 * Per-model token breakdown.
 */
export interface ModelTokenBreakdown {
  /** Model name (e.g., "claude-sonnet-4-5") */
  modelName: string;
  /** Input tokens used by this model */
  inputTokens: number;
  /** Output tokens used by this model */
  outputTokens: number;
  /** Total tokens for this model */
  totalTokens: number;
  /** Number of interactions with this model */
  interactionCount: number;
}

/**
 * Per-agent token breakdown.
 */
export interface AgentTokenBreakdown {
  /** Agent name ("Orchestrator" or "OpenHands") */
  agent: string;
  /** Input tokens used by this agent */
  inputTokens: number;
  /** Output tokens used by this agent */
  outputTokens: number;
  /** Total tokens for this agent */
  totalTokens: number;
  /** Number of interactions by this agent */
  interactionCount: number;
}

/**
 * A single token interaction.
 */
export interface TokenInteraction {
  /** When this interaction occurred */
  timestamp: string;
  /** Which agent made the call ("Orchestrator" or "OpenHands") */
  agent: string;
  /** Input (prompt) tokens consumed */
  inputTokens: number;
  /** Output (completion) tokens consumed */
  outputTokens: number;
  /** Total tokens for this interaction */
  totalTokens: number;
  /** Model name used */
  modelName: string;
  /** Number of tool calls made */
  toolCalls: number;
  /** Large tool responses that contributed to token usage */
  largeToolResponses: string[];
}

/**
 * Token usage data for a single session.
 * Retrieved from GET /api/sessions/{sessionId}/tokens
 */
export interface TokenUsage {
  /** Session identifier */
  sessionId: string;
  /** Total input tokens across all interactions */
  totalInputTokens: number;
  /** Total output tokens across all interactions */
  totalOutputTokens: number;
  /** Total tokens across all sources */
  totalTokens: number;
  /** Token breakdown by model */
  modelBreakdown: ModelTokenBreakdown[];
  /** Token breakdown by agent */
  agentBreakdown: AgentTokenBreakdown[];
  /** Individual token interactions (most recent first) */
  interactions: TokenInteraction[];
  /** Last time token data was updated */
  lastUpdated: string | null;
  /** Estimated total cost for this session (null if no pricing configured) */
  estimatedCost: number | null;
  /** Currency code for the cost (e.g., "USD") */
  currency: string | null;
  /** Cost breakdown per model */
  costByModel: ModelCostBreakdown[] | null;
  /** Models that have usage but no pricing configured */
  modelsWithoutPricing: string[] | null;
}

/**
 * Cost breakdown for a single model.
 */
export interface ModelCostBreakdown {
  /** Model name */
  modelName: string;
  /** Cost of input tokens */
  inputCost: number;
  /** Cost of output tokens */
  outputCost: number;
  /** Total cost for this model */
  totalCost: number;
}

/**
 * Aggregate token statistics across all sessions.
 * Retrieved from GET /api/stats/tokens
 */
export interface TokenStats {
  /** Total input tokens across all sessions */
  totalInputTokens: number;
  /** Total output tokens across all sessions */
  totalOutputTokens: number;
  /** Grand total of all tokens */
  grandTotal: number;
  /** Number of sessions with token data */
  sessionCount: number;
  /** Total number of interactions */
  interactionCount: number;
  /** Token breakdown by model across all sessions */
  modelBreakdown: ModelTokenBreakdown[];
  /** Token breakdown by agent across all sessions */
  agentBreakdown: AgentTokenBreakdown[];
  /** Total estimated cost (null if no pricing configured) */
  totalEstimatedCost: number | null;
  /** Currency code (e.g., "USD") */
  currency: string | null;
  /** Cost breakdown per model */
  costByModel: ModelCostBreakdown[] | null;
  /** Models that have usage but no pricing configured */
  modelsWithoutPricing: string[] | null;
}

/**
 * A single data point in token usage time series.
 */
export interface TokenTimeSeriesDataPoint {
  /** Date/time for this data point */
  date: string;
  /** Input tokens for this period */
  inputTokens: number;
  /** Output tokens for this period */
  outputTokens: number;
  /** Total tokens for this period */
  totalTokens: number;
  /** Number of sessions with activity in this period */
  sessionCount: number;
}

/**
 * Time-series token usage response.
 */
export interface TokenTimeSeriesResponse {
  /** Start of date range */
  from: string;
  /** End of date range */
  to: string;
  /** Granularity used (day or hour) */
  granularity: 'day' | 'hour';
  /** Time series data points */
  data: TokenTimeSeriesDataPoint[];
}

/**
 * Token usage details for a top session.
 */
export interface TopSessionTokenUsage {
  /** Session ID */
  sessionId: string;
  /** Total tokens used */
  totalTokens: number;
  /** Token breakdown by model */
  modelBreakdown: ModelTokenBreakdown[];
  /** Token breakdown by agent */
  agentBreakdown: AgentTokenBreakdown[];
  /** When token data was last updated */
  lastUpdated: string | null;
  /** Session summary/title */
  summary?: string;
  /** Last user message */
  lastUserMessage?: string;
  /** Project name */
  projectName?: string;
  /** When session was created */
  createdAt?: string;
  /** Estimated cost for this session (null if no pricing) */
  estimatedCost: number | null;
  /** Currency code */
  currency: string | null;
}

/**
 * Response for top sessions by token usage.
 */
export interface TopSessionsResponse {
  /** Sessions sorted by total tokens descending */
  sessions: TopSessionTokenUsage[];
}
